import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronRight, ChevronDown, FolderOpen, Folder, Plus, Edit, Trash2, Grid } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function CollectionTree({ 
  collections = [], 
  selectedCollection, 
  onSelectCollection,
  onAddSubcollection,
  onEditCollection,
  onDeleteCollection,
  productCounts = {}
}) {
  const [expandedCollections, setExpandedCollections] = useState(new Set());
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  const toggleExpanded = (collectionId) => {
    const newExpanded = new Set(expandedCollections);
    if (newExpanded.has(collectionId)) {
      newExpanded.delete(collectionId);
    } else {
      newExpanded.add(collectionId);
    }
    setExpandedCollections(newExpanded);
  };
  
  // Build tree structure
  const buildTree = () => {
    const tree = [];
    const map = {};
    
    // Create map of all collections
    collections.forEach(col => {
      map[col.collection_id] = { ...col, children: [] };
    });
    
    // Build hierarchy
    collections.forEach(col => {
      if (col.parent_id && map[col.parent_id]) {
        map[col.parent_id].children.push(map[col.collection_id]);
      } else if (!col.parent_id) {
        tree.push(map[col.collection_id]);
      }
    });
    
    return tree;
  };
  
  const renderCollection = (collection, level = 0) => {
    const isExpanded = expandedCollections.has(collection.collection_id);
    const hasChildren = collection.children && collection.children.length > 0;
    const isSelected = selectedCollection === collection.collection_id;
    const count = productCounts[collection.collection_id] || 0;
    
    return (
      <div key={collection.collection_id} className="select-none">
        <div
          data-testid={`collection-item-${collection.collection_id}`}
          className={`
            flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer group
            hover:bg-accent transition-colors
            ${isSelected ? 'bg-accent border-l-2 border-primary' : ''}
          `}
          style={{ paddingLeft: `${level * 20 + 12}px` }}
        >
          {/* Expand/Collapse Button */}
          {hasChildren ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleExpanded(collection.collection_id);
              }}
              className="hover:bg-muted rounded p-0.5"
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>
          ) : (
            <div className="w-4 h-4" />
          )}
          
          {/* Folder Icon */}
          <div onClick={() => onSelectCollection(collection.collection_id)}>
            {isExpanded ? (
              <FolderOpen className="w-4 h-4 text-primary" />
            ) : (
              <Folder className="w-4 h-4 text-muted-foreground" />
            )}
          </div>
          
          {/* Collection Name */}
          <div 
            className="flex-1 text-sm font-medium truncate"
            onClick={() => onSelectCollection(collection.collection_id)}
          >
            {collection.name}
          </div>
          
          {/* Product Count */}
          {count > 0 && (
            <span className="text-xs text-muted-foreground">
              {count}
            </span>
          )}
          
          {/* Actions (show on hover) */}
          <div className="hidden group-hover:flex gap-1">
            <button
              data-testid={`add-subcollection-${collection.collection_id}`}
              onClick={(e) => {
                e.stopPropagation();
                onAddSubcollection(collection.collection_id);
              }}
              className="p-1 hover:bg-muted rounded"
              title="Add subcollection"
            >
              <Plus className="w-3 h-3" />
            </button>
            <button
              data-testid={`edit-collection-${collection.collection_id}`}
              onClick={(e) => {
                e.stopPropagation();
                onEditCollection(collection);
              }}
              className="p-1 hover:bg-muted rounded"
              title="Edit"
            >
              <Edit className="w-3 h-3" />
            </button>
            <button
              data-testid={`delete-collection-${collection.collection_id}`}
              onClick={(e) => {
                e.stopPropagation();
                onDeleteCollection(collection.collection_id);
              }}
              className="p-1 hover:bg-destructive/10 text-destructive rounded"
              title="Delete"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        </div>
        
        {/* Children */}
        <AnimatePresence>
          {isExpanded && hasChildren && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {collection.children.map(child => renderCollection(child, level + 1))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };
  
  const tree = buildTree();
  
  if (isCollapsed) {
    return (
      <div className="w-12 border-r border-border bg-card p-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsCollapsed(false)}
          className="w-full"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    );
  }
  
  return (
    <div className="w-64 border-r border-border bg-card flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border flex items-center justify-between">
        <h3 className="font-outfit font-bold text-sm">Collections</h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsCollapsed(true)}
          className="h-6 w-6 p-0"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
      
      {/* All Products Option */}
      <div
        data-testid="collection-all-products"
        onClick={() => onSelectCollection(null)}
        className={`
          flex items-center gap-2 px-3 py-2 mx-2 mt-2 rounded-lg cursor-pointer
          hover:bg-accent transition-colors
          ${selectedCollection === null ? 'bg-accent border-l-2 border-primary' : ''}
        `}
      >
        <Grid className="w-4 h-4 text-primary" />
        <span className="text-sm font-medium">All Products</span>
        {productCounts['uncategorized'] > 0 && (
          <span className="ml-auto text-xs text-muted-foreground">
            {productCounts['uncategorized']}
          </span>
        )}
      </div>
      
      {/* Collections Tree */}
      <div className="flex-1 overflow-y-auto p-2">
        {tree.length === 0 ? (
          <div className="text-center py-8 px-4">
            <Folder className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">No collections yet</p>
          </div>
        ) : (
          tree.map(collection => renderCollection(collection))
        )}
      </div>
    </div>
  );
}
